import React, { useState, useEffect } from 'react';
import { packageAPI, customerAPI } from '../api/axios';
import { History, Filter, X, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import './PackageHistoryPage.css';

const PackageHistoryPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalPages: 0,
    totalElements: 0
  });

  // 필터 상태
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    customerId: '',
    customerName: ''
  });

  // 고객 검색 상태
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);

  // 거래 목록 조회
  const loadTransactions = async (page = 0) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        size: 20
      };

      if (filters.type) {
        params.type = filters.type;
      }

      if (filters.customerId) {
        params.customerId = filters.customerId;
      }

      const response = await packageAPI.getAllTransactions(params);
      setTransactions(response.data.content);
      setPagination({
        page: response.data.number,
        size: response.data.size,
        totalPages: response.data.totalPages,
        totalElements: response.data.totalElements
      });
    } catch (error) {
      console.error('거래 이력 조회 실패:', error);
      alert('거래 이력을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 초기 로드
  useEffect(() => {
    loadTransactions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 필터 적용
  const handleApplyFilters = () => {
    loadTransactions(0);
    setShowFilters(false);
  };

  // 필터 초기화
  const handleResetFilters = () => {
    setFilters({
      type: '',
      customerId: '',
      customerName: ''
    });
    setCustomerSearchTerm('');
    loadTransactions(0);
  };

  // 고객 검색
  const handleCustomerSearch = async () => {
    if (!customerSearchTerm.trim()) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      const response = await customerAPI.search(customerSearchTerm);
      setCustomerSearchResults(response.data);
    } catch (error) {
      console.error('고객 검색 실패:', error);
    }
  };

  // 고객 선택
  const handleSelectCustomer = (customer) => {
    setFilters({
      ...filters,
      customerId: customer.id,
      customerName: customer.name
    });
    setCustomerSearchResults([]);
    setCustomerSearchTerm('');
  };

  // 고객 필터 제거
  const handleRemoveCustomerFilter = () => {
    setFilters({
      ...filters,
      customerId: '',
      customerName: ''
    });
  };

  // 거래 유형 한글 변환
  const getTransactionTypeLabel = (type) => {
    const labels = {
      PURCHASE: '구매',
      USAGE: '사용',
      REFUND: '환불',
      ADJUSTMENT: '조정'
    };
    return labels[type] || type;
  };

  // 거래 유형 색상
  const getTransactionTypeClass = (type) => {
    const classes = {
      PURCHASE: 'purchase',
      USAGE: 'usage',
      REFUND: 'refund',
      ADJUSTMENT: 'adjustment'
    };
    return classes[type] || '';
  };

  // 날짜 포맷
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 페이지 변경
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      loadTransactions(newPage);
    }
  };

  return (
    <div className="package-history-page">
      <div className="page-header">
        <div className="header-title">
          <History size={28} />
          <h1>패키지 거래 이력</h1>
        </div>
        <div className="header-actions">
          <button
            className={`btn-filter ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            필터 {(filters.type || filters.customerId) && '●'}
          </button>
          {(filters.type || filters.customerId) && (
            <button className="btn-reset" onClick={handleResetFilters}>
              <X size={18} />
              초기화
            </button>
          )}
        </div>
      </div>

      {/* 필터 영역 */}
      {showFilters && (
        <div className="filter-section">
          <div className="filter-grid">
            <div className="filter-item">
              <label>거래 유형</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
              >
                <option value="">전체</option>
                <option value="PURCHASE">구매</option>
                <option value="USAGE">사용</option>
                <option value="REFUND">환불</option>
                <option value="ADJUSTMENT">조정</option>
              </select>
            </div>

            <div className="filter-item">
              <label>고객</label>
              <div className="customer-filter">
                {filters.customerName ? (
                  <div className="selected-customer">
                    <span>{filters.customerName}</span>
                    <button onClick={handleRemoveCustomerFilter}>
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="customer-search">
                    <input
                      type="text"
                      placeholder="고객 검색"
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCustomerSearch()}
                    />
                    <button onClick={handleCustomerSearch}>
                      <Search size={16} />
                    </button>
                  </div>
                )}

                {customerSearchResults.length > 0 && (
                  <div className="customer-search-results">
                    {customerSearchResults.map(customer => (
                      <div
                        key={customer.id}
                        className="customer-result-item"
                        onClick={() => handleSelectCustomer(customer)}
                      >
                        <strong>{customer.name}</strong>
                        <span>{customer.phone}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn-secondary" onClick={() => setShowFilters(false)}>
              취소
            </button>
            <button className="btn-primary" onClick={handleApplyFilters}>
              적용
            </button>
          </div>
        </div>
      )}

      {/* 통계 요약 */}
      <div className="summary-bar">
        <span>총 {pagination.totalElements.toLocaleString()}건</span>
        {filters.type && <span>• {getTransactionTypeLabel(filters.type)}</span>}
        {filters.customerName && <span>• {filters.customerName}</span>}
      </div>

      {/* 거래 목록 */}
      {loading ? (
        <div className="loading-state">
          <p>로딩 중...</p>
        </div>
      ) : transactions.length === 0 ? (
        <div className="empty-state">
          <History size={64} />
          <p>거래 이력이 없습니다.</p>
        </div>
      ) : (
        <div className="transaction-table-wrapper">
          <table className="transaction-table">
            <thead>
              <tr>
                <th>일시</th>
                <th>거래 유형</th>
                <th>고객</th>
                <th>패키지</th>
                <th>변화량</th>
                <th>잔액</th>
                <th>금액</th>
                <th>담당자</th>
                <th>설명</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(transaction => (
                <tr key={transaction.id}>
                  <td>{formatDate(transaction.transactionDate)}</td>
                  <td>
                    <span className={`transaction-badge ${getTransactionTypeClass(transaction.transactionType)}`}>
                      {getTransactionTypeLabel(transaction.transactionType)}
                    </span>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <strong>{transaction.customerName}</strong>
                      <small>{transaction.customerPhone}</small>
                    </div>
                  </td>
                  <td>
                    <div className="package-cell">
                      <span>{transaction.packageName}</span>
                      <small className={transaction.packageType === 'COUNT_BASED' ? 'count-based' : 'amount-based'}>
                        {transaction.packageType === 'COUNT_BASED' ? '횟수권' : '정액권'}
                      </small>
                    </div>
                  </td>
                  <td>
                    <span className={transaction.countChange > 0 || transaction.amountChange > 0 ? 'positive' : 'negative'}>
                      {transaction.packageType === 'COUNT_BASED'
                        ? `${transaction.countChange > 0 ? '+' : ''}${transaction.countChange}회`
                        : `${transaction.amountChange > 0 ? '+' : ''}${transaction.amountChange?.toLocaleString()}원`}
                    </span>
                  </td>
                  <td>
                    {transaction.packageType === 'COUNT_BASED'
                      ? `${transaction.afterCount}회`
                      : `${transaction.afterAmount?.toLocaleString()}원`}
                  </td>
                  <td>
                    {transaction.paymentAmount ? (
                      <strong className="payment-amount">
                        {transaction.paymentAmount.toLocaleString()}원
                      </strong>
                    ) : '-'}
                  </td>
                  <td>{transaction.adminName}</td>
                  <td className="description-cell">{transaction.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 페이징 */}
      {!loading && transactions.length > 0 && (
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 0}
          >
            <ChevronLeft size={20} />
          </button>
          <span className="page-info">
            {pagination.page + 1} / {pagination.totalPages}
          </span>
          <button
            className="page-btn"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages - 1}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PackageHistoryPage;
